import cloneDeep from 'lodash/cloneDeep';

import type { Thunk } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';
import applyLinkedToGroups from 'editor/src/store/variants/helpers/applyLinkedToGroups';
import getProductDataVariationGroups from 'editor/src/store/variants/helpers/getProductDataVariationGroups';
import removeSpreadsWithoutPages, {
  SpreadsWithoutPagesReport,
} from 'editor/src/store/variants/helpers/removeSpreadsWithoutPages';
import { ProductsDataPayload } from 'editor/src/store/variants/reducer/setProductsDataReducer';
import { setProductsDataAction, setSelectedProductIdAction } from 'editor/src/store/variants/slice';
import { ExistingVariant, MultipleProducts } from 'editor/src/store/variants/types';

import { captureException, captureMessage } from 'editor/src/logger';
import applyLayoutPerProductUid from 'editor/src/util/layouts/applyLayoutPerProductUid';

import { batch } from '../../batchedSubscribeEnhancer';

import setProductDataOperation from './setProductDataOperation';

const setMultipleProductsDataOperation =
  (data: ProductsDataPayload): Thunk =>
  (dispatch, getState: () => RootState, { i18n }) => {
    const { products, selectedProductId } = data;
    const activeProduct = products[selectedProductId];

    if (!activeProduct) {
      captureException(new Error('No selectedProductId in multiple products set'), {
        extra: {
          products,
          selectedProductId,
        },
      });
      return;
    }

    const state = getState();
    const variantMultipleProducts: MultipleProducts = {};
    // Before the groups are built from them: the groups are kept and exported to the host as they are, so a
    // spread without pages left here breaks every step that reads them, not only the open product's load.
    const spreadsWithoutPages: SpreadsWithoutPagesReport[] = [];
    Object.entries(products).forEach(([productId, productData]) => {
      const {
        configuration,
        existingVariants = [],
        layoutPerProductUids = {},
        groupedSpreadsPerProductUids = {},
      } = productData;
      spreadsWithoutPages.push(...removeSpreadsWithoutPages(existingVariants, groupedSpreadsPerProductUids));
      const updatedVariants: ExistingVariant[] = [];

      existingVariants.forEach((variant: ExistingVariant) => {
        if (!variant.designData || !layoutPerProductUids[variant.designData.product_uid]) {
          updatedVariants.push(variant);
          return;
        }
        const variantClone = cloneDeep(variant);
        if (variantClone.designData) {
          variantClone.designData = applyLayoutPerProductUid(
            variantClone.designData,
            state,
            true,
            i18n,
            layoutPerProductUids,
            groupedSpreadsPerProductUids,
            configuration.forceLayout,
          );
        }
        updatedVariants.push(variantClone);
      });

      // A product other than the open one is exported to the host as saved, so its variants must keep their designs
      // until it is opened and its templates are requested.
      const groups =
        getProductDataVariationGroups(productData, updatedVariants, state, i18n, productId !== selectedProductId) || [];
      applyLinkedToGroups(groups, productData.defaultVariationsInfo);
      variantMultipleProducts[productId] = Object.assign(productData, { variantGroups: groups });
    });

    if (spreadsWithoutPages.length) {
      captureMessage('Removed variant design spreads without pages', { extra: { variants: spreadsWithoutPages } });
    }

    batch(() => {
      dispatch(setProductsDataAction(variantMultipleProducts));
      dispatch(setSelectedProductIdAction(selectedProductId));
      void dispatch(setProductDataOperation(cloneDeep(activeProduct)));
    });
  };

export default setMultipleProductsDataOperation;
